import { useCheckRole } from '@ospace/auth'
import { userTypes } from '@ospace/core-auth'
import { PageLink, PageTitle } from '@ospace/layout'
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core'
import { IconBomb, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ConfirmationModal, ErrorBoundary, showNotification } from '@ospace/shared'
import {
  useClearCrawlCache,
  useDeduplicationSettings,
  useHubspotOwners,
  useNukeDomainCache,
  useUpdateReviewTasksEnabled,
  useUpdateTaskOwner,
} from '../hooks/useDeduplicationSettings'
import type { HubspotOwnerOption } from '../types'

const ownerLabel = (owner: HubspotOwnerOption) =>
  owner.email ? `${owner.name} (${owner.email})` : owner.name

const SettingsPage = () => {
  const {
    data: settings,
    isLoading: settingsLoading,
    status: settingsStatus,
    error: settingsError,
  } = useDeduplicationSettings()
  const {
    data: ownersData,
    isLoading: ownersLoading,
    status: ownersStatus,
    error: ownersError,
  } = useHubspotOwners()
  const updateTaskOwner = useUpdateTaskOwner()
  const updateReviewTasksEnabled = useUpdateReviewTasksEnabled()
  const clearCrawlCache = useClearCrawlCache()
  const nukeDomainCache = useNukeDomainCache()
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null)
  const [reviewTasksEnabled, setReviewTasksEnabled] = useState(true)

  const ownerOptions = useMemo(
    () =>
      (ownersData?.results ?? []).map((owner) => ({
        value: owner.id,
        label: ownerLabel(owner),
      })),
    [ownersData]
  )

  const ownersById = useMemo(
    () => new Map((ownersData?.results ?? []).map((owner) => [owner.id, owner])),
    [ownersData]
  )

  useEffect(() => {
    if (settings?.taskOwner?.ownerId) {
      setSelectedOwnerId(settings.taskOwner.ownerId)
    }
  }, [settings?.taskOwner?.ownerId])

  useEffect(() => {
    if (settings?.reviewTasksEnabled != null) {
      setReviewTasksEnabled(settings.reviewTasksEnabled)
    }
  }, [settings?.reviewTasksEnabled])

  const breadcrumbs: PageLink[] = [
    { title: 'Company De-duplication', path: '/de-duplication/companies', isActive: false },
    { title: 'Settings', path: '/de-duplication/companies/settings', isActive: true },
  ]

  const handleReviewTasksToggle = async (enabled: boolean) => {
    setReviewTasksEnabled(enabled)

    try {
      await updateReviewTasksEnabled.mutateAsync(enabled)
      showNotification({
        type: 'success',
        message: enabled ? 'Review tasks enabled' : 'Review tasks disabled',
      })
    } catch {
      setReviewTasksEnabled(!enabled)
      showNotification({ type: 'error', message: 'Failed to update review tasks setting' })
    }
  }

  const handleSave = async () => {
    if (!selectedOwnerId) {
      showNotification({ type: 'error', message: 'Select a HubSpot task owner' })
      return
    }

    const owner = ownersById.get(selectedOwnerId)
    if (!owner) {
      showNotification({ type: 'error', message: 'Selected owner not found' })
      return
    }

    try {
      await updateTaskOwner.mutateAsync({
        ownerId: owner.id,
        ownerName: owner.name,
        ownerEmail: owner.email,
      })
      showNotification({ type: 'success', message: 'Task owner saved' })
    } catch {
      showNotification({ type: 'error', message: 'Failed to save task owner' })
    }
  }

  const sourceLabel =
    settings?.source === 'database'
      ? 'Saved in database'
      : settings?.source === 'env_id'
        ? 'From HUBSPOT_TASK_OWNER_ID env var'
        : settings?.source === 'env_email'
          ? 'From HUBSPOT_TASK_OWNER_EMAIL env var'
          : 'Not configured'

  if (settingsLoading) {
    return (
      <Group justify='center' py='xl'>
        <Loader />
      </Group>
    )
  }

  if (settingsStatus === 'error') {
    return (
      <Stack gap='sm'>
        <Text c='red'>Failed to load settings.</Text>
        <Text size='sm' c='dimmed'>
          {(settingsError as Error | undefined)?.message ??
            'Check the API is deployed and you have admin access.'}
        </Text>
      </Stack>
    )
  }

  if (ownersLoading && !ownersData) {
    return (
      <Group justify='center' py='xl'>
        <Loader />
      </Group>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>De-duplication settings</PageTitle>

      <Anchor component={Link} to='/de-duplication/companies' mb='md' display='inline-block'>
        ← Back to observer
      </Anchor>

      <Paper withBorder p='lg' maw={640} mb='lg'>
        <Stack gap='md'>
          <div>
            <Text fw={700} size='lg' mb={4}>
              Review tasks
            </Text>
            <Text size='sm' c='dimmed'>
              When enabled, FLAG_FOR_REVIEW groups create HubSpot tasks. Requires a task owner
              below.
            </Text>
          </div>

          <Switch
            label='Create HubSpot review tasks'
            description={
              reviewTasksEnabled && !settings?.reviewTasksCanRun
                ? 'Enabled, but no task owner is assigned yet — tasks will not be created.'
                : reviewTasksEnabled
                  ? 'Review tasks will be created during runs.'
                  : 'Review tasks will not be created; groups may still merge, delete, or update domains.'
            }
            checked={reviewTasksEnabled}
            onChange={(event) => handleReviewTasksToggle(event.currentTarget.checked)}
            disabled={updateReviewTasksEnabled.isLoading}
          />
        </Stack>
      </Paper>

      <Paper withBorder p='lg' maw={640} mb='lg'>
        <Stack gap='md'>
          <div>
            <Text fw={700} size='lg' mb={4}>
              HubSpot task owner
            </Text>
            <Text size='sm' c='dimmed'>
              Owner assigned to FLAG_FOR_REVIEW tasks when review tasks are enabled.
            </Text>
          </div>

          {settings?.source && settings.source !== 'database' && settings.taskOwner && (
            <Alert color='blue' title='Current effective owner'>
              {ownerLabel({
                id: settings.taskOwner.ownerId,
                name: settings.taskOwner.ownerName,
                email: settings.taskOwner.ownerEmail,
              })}{' '}
              · {sourceLabel}. Save below to persist in the database.
            </Alert>
          )}

          <Select
            label='Task owner'
            description='Search by name or email'
            placeholder={
              ownersStatus === 'error' ? 'Failed to load HubSpot owners' : 'Select HubSpot owner'
            }
            data={ownerOptions}
            value={selectedOwnerId}
            onChange={setSelectedOwnerId}
            searchable
            nothingFoundMessage='No owners found'
            clearable
            disabled={ownersStatus === 'error' || !reviewTasksEnabled}
          />

          {ownersStatus === 'error' && (
            <Text size='sm' c='red'>
              {(ownersError as Error | undefined)?.message ??
                'Could not load HubSpot owners. Redeploy the API stack and confirm HUBSPOT_API_KEY is set.'}
            </Text>
          )}

          <Group justify='space-between'>
            <Text size='xs' c='dimmed'>
              Current source: {sourceLabel}
            </Text>
            <Button
              loading={updateTaskOwner.isLoading}
              onClick={handleSave}
              disabled={!reviewTasksEnabled}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p='lg' maw={640}>
        <Stack gap='md'>
          <div>
            <Text fw={700} size='lg' mb={4}>
              Domain cache
            </Text>
            <Text size='sm' c='dimmed'>
              DynamoDB stores crawl results, processed-action markers, and OpenAI pair-resolution
              cache. Use the targeted clear for fresh crawls only, or nuke everything to fully reset
              idempotency state.
            </Text>
          </div>

          <Alert color='yellow' title='Clear crawl cache'>
            Removes <code>:crawled</code> entries only. Processed-action markers and OpenAI cache
            are kept.
          </Alert>

          <Group justify='flex-end'>
            <ConfirmationModal
              alertMessage={
                <Text size='sm'>
                  This deletes all cached domain crawl results. The next run will re-fetch and
                  re-classify every company domain.
                </Text>
              }
              isLoading={clearCrawlCache.isLoading}
              action={async () => {
                try {
                  const result = await clearCrawlCache.mutateAsync()
                  showNotification({
                    title: 'Crawl cache cleared',
                    message: result.message,
                    type: 'success',
                  })
                } catch (error) {
                  showNotification({
                    title: 'Failed to clear crawl cache',
                    message: error instanceof Error ? error.message : 'Unknown error',
                    type: 'error',
                  })
                }
              }}
              renderButton={({ onClick }) => (
                <Button
                  color='red'
                  variant='light'
                  leftSection={<IconTrash size={16} />}
                  onClick={onClick}
                  loading={clearCrawlCache.isLoading}
                  disabled={nukeDomainCache.isLoading}
                >
                  Clear crawl cache
                </Button>
              )}
            />
          </Group>

          <Alert color='red' title='Nuke entire cache'>
            Deletes <strong>every row</strong> in the domain table — crawl cache, processed-action
            markers (<code>:DELETE</code>, <code>:MERGE</code>, etc.), and OpenAI{' '}
            <code>COMPARE_COMPANIES</code> entries. The next run will re-process everything and may
            re-execute HubSpot actions that were previously completed.
          </Alert>

          <Group justify='flex-end'>
            <ConfirmationModal
              alertMessage={
                <Text size='sm'>
                  This permanently deletes <strong>all</strong> deduplication domain cache data.
                  Idempotency guards are removed — duplicate HubSpot actions may run again on the
                  next pass.
                </Text>
              }
              isLoading={nukeDomainCache.isLoading}
              action={async () => {
                try {
                  const result = await nukeDomainCache.mutateAsync()
                  showNotification({
                    title: 'Domain cache nuked',
                    message: result.message,
                    type: 'success',
                  })
                } catch (error) {
                  showNotification({
                    title: 'Failed to nuke domain cache',
                    message: error instanceof Error ? error.message : 'Unknown error',
                    type: 'error',
                  })
                }
              }}
              renderButton={({ onClick }) => (
                <Button
                  color='red'
                  leftSection={<IconBomb size={16} />}
                  onClick={onClick}
                  loading={nukeDomainCache.isLoading}
                  disabled={clearCrawlCache.isLoading}
                >
                  Nuke entire cache
                </Button>
              )}
            />
          </Group>
        </Stack>
      </Paper>
    </>
  )
}

const Controller = () => {
  useCheckRole([userTypes.Admin])
  return <SettingsPage />
}

export const SettingsPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
